import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import Messaging from '../components/Messaging/Messaging';
import './Dashboard.css';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'default', icon: '○' },
  pending: { label: 'Under Review', color: 'pending', icon: '⏳' },
  under_review: { label: 'Active Review', color: 'review', icon: '🔍' },
  approved: { label: 'Approved', color: 'approved', icon: '✓' },
  rejected: { label: 'Not Approved', color: 'rejected', icon: '✕' },
};

const TABS = ['Overview', 'My Application', 'Messages', 'Profile'];

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    loadProfile();
    loadUnread();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/users/profile');
      setProfile(res.data);
      setProfileForm(res.data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const loadUnread = async () => {
    try {
      const res = await api.get('/messages/unread-count');
      setUnreadCount(res.data.count);
    } catch { }
  };

  const saveProfile = async () => {
    try {
      await api.put('/users/profile', profileForm);
      setSaveMsg('Profile updated successfully.');
      setEditMode(false);
      loadProfile();
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Failed to save. Please try again.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) return (
    <div className="dash-page-wrapper">
      <div className="dash-loading">
        <div className="spinner" />
      </div>
    </div>
  );

  const status = STATUS_CONFIG[profile?.application_status] || STATUS_CONFIG.not_started;

  return (
    <div className="dash-page-wrapper">
      {/* Dashboard Top Bar */}
      <div className="dash-topbar">
        <Link to="/" className="dash-topbar-home">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Home
        </Link>
        <span className="dash-topbar-title">Gold Card Portal</span>
        <div className="dash-topbar-user">
          Signed in as <strong>{user.first_name} {user.last_name}</strong>
        </div>
      </div>

      <main className="dashboard-main">
        <div className="dashboard-layout">
          {/* Sidebar */}
          <aside className="dash-sidebar">
            <div className="dash-user-card">
              <div className="dash-avatar">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </div>
              <h3>{user.first_name} {user.last_name}</h3>
              <p className="dash-email">{user.email}</p>
              <span className={`badge badge-${status.color}`}>{status.label}</span>
            </div>

            <nav className="dash-nav">
              {TABS.map(tab => (
                <button
                  key={tab}
                  className={`dash-nav-item ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'Overview' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>}
                  {tab === 'My Application' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
                  {tab === 'Messages' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>}
                  {tab === 'Profile' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>}
                  <span>{tab}</span>
                  {tab === 'Messages' && unreadCount > 0 && (
                    <span className="nav-badge">{unreadCount}</span>
                  )}
                </button>
              ))}
            </nav>

            <button className="dash-logout-btn" onClick={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              Sign Out
            </button>
          </aside>

          {/* Main content */}
          <div className="dash-content">

            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'Overview' && (
              <div className="dash-panel">
                <div className="dash-panel-header">
                  <h2>Welcome back, {user.first_name}</h2>
                  <p>Here's a summary of your Gold Card application.</p>
                </div>

                {/* Status tracker */}
                <div className="status-tracker">
                  {['not_started', 'pending', 'under_review', 'approved'].map((s, i) => {
                    const statuses = ['not_started', 'pending', 'under_review', 'approved', 'rejected'];
                    const currentIdx = statuses.indexOf(profile?.application_status);
                    const stepIdx = ['not_started', 'pending', 'under_review', 'approved'].indexOf(s);
                    const isCompleted = currentIdx > stepIdx || profile?.application_status === s;
                    const isCurrent = profile?.application_status === s;
                    return (
                      <React.Fragment key={s}>
                        <div className={`status-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                          <div className="step-circle">
                            {isCompleted && !isCurrent
                              ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="14"><path d="M20 6L9 17l-5-5" /></svg>
                              : i + 1
                            }
                          </div>
                          <span className="step-label">{STATUS_CONFIG[s]?.label}</span>
                        </div>
                        {i < 3 && <div className={`step-connector ${currentIdx > i ? 'completed' : ''}`} />}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Stat cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-blue">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Application</div>
                      <div className="stat-value">{profile?.card_type ? profile.card_type.charAt(0).toUpperCase() + profile.card_type.slice(1) : 'Not Started'}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-gold">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Payment</div>
                      <div className="stat-value">{profile?.payment_status === 'paid' ? 'Paid' : 'Pending'}</div>
                    </div>
                  </div>
                  <div className="stat-card" onClick={() => setActiveTab('Messages')} style={{ cursor: 'pointer' }}>
                    <div className="stat-icon stat-icon-navy">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Unread Messages</div>
                      <div className="stat-value">{unreadCount}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-green">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Member Since</div>
                      <div className="stat-value">{new Date(profile?.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                    </div>
                  </div>
                </div>

                {/* Quick actions */}
                {profile?.application_status === 'not_started' && (
                  <div className="action-banner">
                    <div>
                      <strong>Start Your Gold Card Application</strong>
                      <p>Submit Form I-140G to begin the vetting process.</p>
                    </div>
                    <button className="btn btn-gold" onClick={() => setActiveTab('My Application')}>
                      Begin Application
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── APPLICATION TAB ── */}
            {activeTab === 'My Application' && (
              <ApplicationForm profile={profile} onSubmit={loadProfile} />
            )}

            {/* ── MESSAGES TAB ── */}
            {activeTab === 'Messages' && (
              <div className="dash-panel">
                <div className="dash-panel-header">
                  <h2>Messages</h2>
                  <p>Communicate directly with your USCIS case officer.</p>
                </div>
                <Messaging />
              </div>
            )}

            {/* ── PROFILE TAB ── */}
            {activeTab === 'Profile' && (
              <div className="dash-panel">
                <div className="dash-panel-header">
                  <h2>My Profile</h2>
                  <p>Update your personal information.</p>
                </div>
                {saveMsg && (
                  <div className={`save-msg ${saveMsg.includes('Failed') ? 'error' : 'success'}`}>
                    {saveMsg}
                  </div>
                )}
                <div className="profile-form-grid">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input className="form-input" value={profileForm.first_name || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, first_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input className="form-input" value={profileForm.last_name || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, last_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input className="form-input" value={profileForm.email || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input className="form-input" value={profileForm.phone || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} />
                  </div>
                  <div className="form-group form-full">
                    <label className="form-label">Street Address</label>
                    <input className="form-input" value={profileForm.address_street || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, address_street: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-input" value={profileForm.address_city || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, address_city: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State / Province</label>
                    <input className="form-input" value={profileForm.address_state || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, address_state: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP / Postal Code</label>
                    <input className="form-input" value={profileForm.address_zip || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, address_zip: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <input className="form-input" value={profileForm.address_country || ''} disabled={!editMode}
                      onChange={e => setProfileForm({ ...profileForm, address_country: e.target.value })} />
                  </div>
                </div>
                <div className="profile-actions">
                  {!editMode
                    ? <button className="btn btn-primary" onClick={() => setEditMode(true)}>Edit Profile</button>
                    : <>
                      <button className="btn btn-primary" onClick={saveProfile}>Save Changes</button>
                      <button className="btn btn-secondary" onClick={() => { setEditMode(false); setProfileForm(profile); }}>Cancel</button>
                    </>
                  }
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}

// ── Inline Application Form Sub-component ──
function ApplicationForm({ profile, onSubmit }) {
  const [form, setForm] = useState({
    card_type: profile?.card_type || 'individual',
    middle_name: '', gender: 'male',
    city_of_birth: '', state_of_birth: '',
    country_of_birth: '', country_of_citizenship: '',
    date_of_birth: '', a_number: '', family_members_count: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/users/application', form);
      setMsg('Application submitted successfully. You will be notified of updates.');
      onSubmit();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitted = ['pending', 'under_review', 'approved', 'rejected'].includes(profile?.application_status);

  return (
    <div className="dash-panel">
      <div className="dash-panel-header">
        <h2>Form I-140G — Gold Card Petition</h2>
        <p>Complete all required fields to submit your Gold Card application.</p>
      </div>

      {isSubmitted && (
        <div className="app-submitted-notice">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          Your application has been submitted. Current status: <strong>{profile.application_status?.replace('_', ' ')}</strong>
        </div>
      )}

      {msg && <div className={`save-msg ${msg.includes('Failed') || msg.includes('failed') ? 'error' : 'success'}`}>{msg}</div>}

      <form onSubmit={handleSubmit}>
        {/* Card Type */}
        <div className="form-section-title">Card Type</div>
        <div className="card-type-selector">
          {[
            { value: 'individual', label: 'Individual Gold Card' },
            { value: 'corporate', label: 'Corporate Gold Card' },
          ].map(ct => (
            <label key={ct.value} className={`card-type-option ${form.card_type === ct.value ? 'active' : ''}`}>
              <input type="radio" name="card_type" value={ct.value}
                checked={form.card_type === ct.value}
                onChange={e => setForm({ ...form, card_type: e.target.value })} />
              {ct.label}
            </label>
          ))}
        </div>

        {/* Recipient Info */}
        <div className="form-section-title">Recipient Information</div>
        <div className="profile-form-grid">
          <div className="form-group">
            <label className="form-label">Middle Name</label>
            <input className="form-input" value={form.middle_name}
              onChange={e => setForm({ ...form, middle_name: e.target.value })} placeholder="Optional" />
          </div>
          <div className="form-group">
            <label className="form-label">Date of Birth *</label>
            <input type="date" className="form-input" value={form.date_of_birth} required
              onChange={e => setForm({ ...form, date_of_birth: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Gender *</label>
            <select className="form-select" value={form.gender}
              onChange={e => setForm({ ...form, gender: e.target.value })}>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">A-Number (Optional)</label>
            <input className="form-input" value={form.a_number} placeholder="A123456789"
              onChange={e => setForm({ ...form, a_number: e.target.value })} />
          </div>

          <div className="form-group">
            <label className="form-label">City of Birth *</label>
            <input className="form-input" required value={form.city_of_birth}
              onChange={e => setForm({ ...form, city_of_birth: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">State/Province of Birth</label>
            <input className="form-input" value={form.state_of_birth}
              onChange={e => setForm({ ...form, state_of_birth: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Country of Birth *</label>
            <input className="form-input" required value={form.country_of_birth}
              onChange={e => setForm({ ...form, country_of_birth: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Country of Citizenship *</label>
            <input className="form-input" required value={form.country_of_citizenship}
              onChange={e => setForm({ ...form, country_of_citizenship: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Family Members (Spouse or Children under 21)</label>
            <select className="form-select" value={form.family_members_count}
              onChange={e => setForm({ ...form, family_members_count: parseInt(e.target.value) })}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <div className="app-disclaimer">
          <strong>Important:</strong> By submitting this petition, you certify under penalty of perjury
          that all information provided is true and correct. Upon payment confirmation, USCIS will
          initiate background vetting per 8 U.S.C. § 1153.
        </div>

        <button type="submit" className="btn btn-primary btn-lg" disabled={submitting || isSubmitted}>
          {submitting ? 'Submitting…' : isSubmitted ? 'Application Submitted' : 'Submit I-140G Petition'}
        </button>
      </form>
    </div>
  );
}